// src/components/HRComponents/KraPerformanceTabs.js
import React, { useMemo } from "react";
import { LayoutDashboard, Library, GitFork, UserCheck } from "lucide-react";
import { MdOutlineRateReview } from "react-icons/md";
import KraOverview from "../../components/HRComponents/KraOverview";
import KraLibrary from "../../components/HRComponents/KraLibrary";
import TagKra from "../../components/HRComponents/TagKra";
import { useTabNavigation } from "../../helpers/useTabNavigation";
import EmployeeKraDashboard from "../../components/HRComponents/EmployeeKraDashboard";
import MyReviews from "../../components/HRComponents/MyReviews";
import TeamReviews from "../../components/HRComponents/TeamReviews";
import KRAReviewWorkflow from "../../components/HRComponents/KRAReviewWorkflow";
import { usePermission } from "../../hooks/usePermission";
import { FaUserTag } from "react-icons/fa6";
import KraAssignmentsList from "../../components/HRComponents/KraAssignmentsList";
import { GoChecklist } from "react-icons/go";
import KraReviewsList from "../../components/HRComponents/KraReviewsList";

const KraPerformanceTabs = () => {
  const { hasPermission, user } = usePermission();

  const tabs = useMemo(() => {
    const allTabs = [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: <LayoutDashboard className="h-4 w-4" />,
        // description: "Your Appraisal & Goal Performance Analytics dashboard",
        content: <EmployeeKraDashboard user={user} />
      },
      {
        id: "kra-review",
        label: "KRA Review",
        icon: <MdOutlineRateReview className="h-4 w-4" />,
        // description: "Review & Heirached Appraisal Workflow",
        content: <MyReviews user={user} />
      },
      {
        id: "team-review",
        label: "Team",
        icon: <MdOutlineRateReview className="h-4 w-4" />,
        // description: "Review & Heirached Appraisal Workflow",
        content: <TeamReviews user={user} />
      },
    ];

    const adminTabs = [
      {
        id: "kra-overview",
        label: "KRA Overview",
        icon: <LayoutDashboard className="h-4 w-4" />,
        // description: "Appraisal & Goal Performance Analytics",
        content: <KraOverview user={user} />,
        permissionKey: "hr:kra-overview",
        action: "view"
      },
      {
        id: "kra-library",
        label: "KRA Library",
        icon: <Library className="h-4 w-4" />,
        // description: "Manage KRA Templates & Reusable Bundles",
        content: <KraLibrary user={user} />,
        permissionKey: "hr:kra-library",
        action: "view"
      },
      {
        id: "tag_kra",
        label: "Tag KRA",
        icon: <FaUserTag className="h-4 w-4" />,
        // description: "Assign KRA goals or Bundles to employees",
        content: <TagKra user={user} />,
        permissionKey: "hr:tag-kra",
        action: "view"
      },
      {
        id: "tag_kra_list",
        label: "Tagged KRA List",
        icon: <UserCheck className="h-4 w-4" />,
        // description: "View tagged KRA goals or Bundles to employees",
        content: <KraAssignmentsList user={user} />,
        permissionKey: "hr:tag-kra-list",
        action: "view"
      },
      {
        id: "review_list",
        label: "Review List",
        icon: <GoChecklist className="h-4 w-4" />,
        // description: "View tagged KRA goals or Bundles to employees",
        content: <KraReviewsList user={user} />,
        permissionKey: "hr:review-list",
        action: "view"
      },
      {
        id: "workflows",
        label: "Workflows",
        icon: <GitFork className="h-4 w-4" />,
        // description: "Dynamic Appraisal Process Workflows",
        content: <KRAReviewWorkflow />,
        permissionKey: "hr:workflows",
        action: "view"
      },      
    ];

    adminTabs.forEach(tab => {
      // 🔐 ஓனருக்கு எந்த பெர்மிஷனும் தேவையில்லை, அவருக்குத் தானாகவே அணுகல் கிடைக்கும்
      const isOwner = user?.role === "owner";

      if (isOwner || hasPermission(tab.permissionKey, tab.action)) {
        allTabs.push(tab);
      }
    });

    return allTabs;
  }, [user, hasPermission]);

  const { activeTab, changeTab } = useTabNavigation(tabs, 'dashboard');
  const activeTabData = tabs.find(tab => tab.id === activeTab) || tabs[0];

  return (
    <div>
      {/* --- TOP TAB NAVIGATION --- */}
      <div className="bg-primary shadow-md sticky top-0 z-20">
        <div className="mx-auto">
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between">
              <nav className="flex space-x-2 overflow-x-auto custom-scrollbar pt-2" aria-label="Tabs">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => changeTab(tab.id)}
                      className={`
                        group inline-flex items-center py-3 px-6 font-bold text-[11px] uppercase tracking-[0.1em]
                        whitespace-nowrap transition-all duration-300 ease-in-out
                        ${isActive
                          ? 'bg-white text-primary rounded-t-xl transform -translate-y-px shadow-sm'
                          : 'text-red-100 hover:text-white hover:bg-red-700 rounded-t-xl'
                        }
                      `}
                    >
                      <span className="mr-2 opacity-70">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="hidden lg:block text-[10px] font-bold text-red-100 uppercase tracking-widest opacity-80 italic">
                {activeTabData?.description}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- SUB-TAB MOUNT CONTENT AREA --- */}
      <div className="mx-auto px-4 lg:px-6 py-6">
        <div className="max-w-[1600px] mx-auto animate-in fade-in duration-500">
          {activeTabData?.content}
        </div>
      </div>
    </div>
  );
};

export default KraPerformanceTabs;